/**
 * Support desk management (OD-65): support agents, single-use device pairing codes (code + QR of
 * desk.hopex.ir/pair#CODE, 15 minutes), and every paired device (agent, device, IP prefix, paired / last
 * seen, push on/off) with an instant revoke. Creating agents and issuing codes need the admin step-up +
 * a reason; revoking a device needs only a reason (protective). Desk agents can never sign in here.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  PageHeader,
  TextField,
} from '@hopex/ui';
import {
  BellOff,
  BellRing,
  Copy,
  ExternalLink,
  KeyRound,
  PenLine,
  ShieldCheck,
  Smartphone,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { api, dt } from '../api.ts';
import { CommandDialog, Facts } from '../lib/command.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';
import { useAdminSession } from '../session.tsx';
import { QrCode } from './Auth.tsx';

interface Agent {
  id: string;
  username: string;
  displayName: string;
  kind: 'ADMIN' | 'DESK';
  status: 'ACTIVE' | 'DISABLED' | 'PENDING_SETUP';
  deskStatus: 'ONLINE' | 'AWAY';
  online: boolean;
  lastSeenAt: string | null;
  openConversations: number;
  activeDevices: number;
  createdAt: string;
  /** OD-71 ticked operation groups. */
  groups?: string[];
}
interface Device {
  id: string;
  label: string;
  device: string | null;
  ipPrefix: string | null;
  agentId: string;
  agent: string;
  username: string;
  pairedAt: string;
  lastSeenAt: string;
  expiresAt: string;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
  revokedAt: string | null;
  revokeReason: string | null;
  push: boolean;
}

/** The desk origin for the pairing link (production: desk.hopex.ir). */
const DESK_URL = (import.meta.env.VITE_DESK_URL as string | undefined) ?? 'https://desk.hopex.ir';

const STATUS_FA = { ACTIVE: 'فعال', EXPIRED: 'منقضی', REVOKED: 'لغوشده' } as const;

export function DeskPage() {
  const agents = useRemote<{ items: Agent[] }>('/desk/agents');
  const devices = useRemote<{ items: Device[] }>('/desk/devices');
  const session = useAdminSession();
  const meId = session.state.status === 'ready' ? session.state.me.adminId : null;
  const [create, setCreate] = useState(false);
  const [pairFor, setPairFor] = useState<{ id: string; name: string } | null>(null);
  const [code, setCode] = useState<{ code: string; expiresAt: string; agent: string } | null>(null);
  const [toggle, setToggle] = useState<Agent | null>(null);
  const [rename, setRename] = useState<Agent | null>(null);
  const [grants, setGrants] = useState<Agent | null>(null);
  const [revoke, setRevoke] = useState<Device | null>(null);
  const reload = () => {
    agents.reload();
    devices.reload();
  };

  return (
    <AppContainer size="content">
      <PageHeader
        title="میز پشتیبانی"
        description="کارشناس‌ها، اتصال یک‌باره‌ی گوشی و دستگاه‌های متصل. کارشناس‌ها فقط پشتیبانی و مشاهده‌ی پرونده‌ی کاربر را دارند و هیچ تغییری نمی‌توانند بدهند."
        actions={
          <div className="flex flex-wrap gap-2">
            {meId && (
              <Button
                variant="secondary"
                onPress={() => setPairFor({ id: meId, name: 'گوشی خودم' })}
                data-desk-pair-self
              >
                <Smartphone aria-hidden className="size-4" /> اتصال گوشی خودم
              </Button>
            )}
            <Button onPress={() => setCreate(true)} data-desk-add-agent>
              <UserPlus aria-hidden className="size-4" /> افزودن کارشناس
            </Button>
          </div>
        }
      />
      <div className="flex flex-col gap-5">
        <Card className="flex flex-col gap-3 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="m-0 text-[15px] font-bold">کارشناس‌ها</h2>
            <a
              href={DESK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent-text hover:underline"
            >
              {DESK_URL.replace(/^https?:\/\//, '')}{' '}
              <ExternalLink aria-hidden className="size-3.5" />
            </a>
          </div>
          <Remote r={agents}>
            {(d) => (
              <DataTable<Agent>
                label="کارشناس‌های میز پشتیبانی"
                rows={d.items}
                density="compact"
                empty={
                  <p className="m-0 p-4 text-[13px] text-fg-muted">
                    هنوز کارشناسی تعریف نشده است. «افزودن کارشناس» یا «اتصال گوشی خودم» را بزنید.
                  </p>
                }
                columns={[
                  {
                    id: 'name',
                    header: 'کارشناس',
                    isRowHeader: true,
                    cell: (a) => (
                      <span className="flex items-center gap-2">
                        <span
                          className={`size-2.5 rounded-full ${a.online ? 'bg-positive' : 'bg-fg-subtle'}`}
                          aria-hidden
                        />
                        <span>
                          <b>{a.displayName}</b>
                          <bdi dir="ltr" className="ms-1.5 font-mono text-[11.5px] text-fg-muted">
                            {a.username}
                          </bdi>
                        </span>
                      </span>
                    ),
                  },
                  {
                    id: 'kind',
                    header: 'نوع',
                    cell: (a) => (
                      <Badge tone={a.kind === 'ADMIN' ? 'accent' : 'neutral'}>
                        {a.kind === 'ADMIN' ? 'مدیر' : 'کارشناس'}
                      </Badge>
                    ),
                  },
                  {
                    id: 'state',
                    header: 'وضعیت',
                    cell: (a) =>
                      a.status !== 'ACTIVE' ? (
                        <Badge tone="negative">غیرفعال</Badge>
                      ) : a.online ? (
                        <Badge tone="positive">آنلاین</Badge>
                      ) : (
                        'دور از میز'
                      ),
                  },
                  {
                    id: 'open',
                    header: 'گفتگوی باز',
                    numeric: true,
                    cell: (a) => a.openConversations.toLocaleString('fa-IR'),
                  },
                  {
                    id: 'devices',
                    header: 'دستگاه فعال',
                    numeric: true,
                    cell: (a) => a.activeDevices.toLocaleString('fa-IR'),
                  },
                  { id: 'seen', header: 'آخرین حضور', cell: (a) => dt(a.lastSeenAt) },
                  {
                    id: 'actions',
                    header: 'کارها',
                    cell: (a) => (
                      <span className="flex flex-wrap gap-1.5">
                        {a.status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onPress={() => setPairFor({ id: a.id, name: a.displayName })}
                            data-desk-pair={a.username}
                          >
                            <KeyRound aria-hidden className="size-3.5" /> کد اتصال
                          </Button>
                        )}
                        {(a.kind === 'DESK' || a.id === meId) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onPress={() => setRename(a)}
                            data-desk-rename={a.username}
                          >
                            <PenLine aria-hidden className="size-3.5" /> ویرایش نام
                          </Button>
                        )}
                        {a.kind === 'DESK' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onPress={() => setGrants(a)}
                            data-desk-grants={a.username}
                          >
                            <ShieldCheck aria-hidden className="size-3.5" /> دسترسی‌ها
                            {a.groups && a.groups.length > 0 && (
                              <span className="hx-num">
                                ({a.groups.length.toLocaleString('fa-IR')})
                              </span>
                            )}
                          </Button>
                        )}
                        {a.kind === 'DESK' && (
                          <Button
                            size="sm"
                            variant={a.status === 'ACTIVE' ? 'ghost' : 'positive'}
                            onPress={() => setToggle(a)}
                          >
                            {a.status === 'ACTIVE' ? 'غیرفعال کردن' : 'فعال کردن'}
                          </Button>
                        )}
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </Remote>
        </Card>

        <Card className="flex flex-col gap-3 p-4 sm:p-5">
          <h2 className="m-0 text-[15px] font-bold">دستگاه‌های متصل</h2>
          <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
            هر دستگاه با کلیدی که فقط روی همان دستگاه است وارد می‌شود. دستگاه گم‌شده یا ناشناس را
            همین‌جا فوراً لغو کنید؛ اتصال هر دستگاه تازه در تلگرام عملیات هم اعلام می‌شود.
          </p>
          <Remote r={devices}>
            {(d) => (
              <DataTable<Device>
                label="دستگاه‌های میز پشتیبانی"
                rows={d.items}
                density="compact"
                empty={
                  <p className="m-0 p-4 text-[13px] text-fg-muted">هنوز دستگاهی متصل نشده است.</p>
                }
                columns={[
                  { id: 'agent', header: 'کارشناس', isRowHeader: true, cell: (x) => x.agent },
                  {
                    id: 'device',
                    header: 'دستگاه',
                    cell: (x) => (
                      <span>
                        <b>{x.label}</b>
                        <span className="block text-[11.5px] text-fg-muted">{x.device ?? '—'}</span>
                      </span>
                    ),
                  },
                  {
                    id: 'ip',
                    header: 'شبکه',
                    cell: (x) => (
                      <bdi dir="ltr" className="font-mono text-[11.5px]">
                        {x.ipPrefix ?? '—'}
                      </bdi>
                    ),
                  },
                  { id: 'paired', header: 'اتصال', cell: (x) => dt(x.pairedAt) },
                  { id: 'seen', header: 'آخرین استفاده', cell: (x) => dt(x.lastSeenAt) },
                  {
                    id: 'push',
                    header: 'اعلان',
                    cell: (x) =>
                      x.push ? (
                        <BellRing aria-label="اعلان روشن" className="size-4 text-positive" />
                      ) : (
                        <BellOff aria-label="اعلان خاموش" className="size-4 text-fg-subtle" />
                      ),
                  },
                  {
                    id: 'status',
                    header: 'وضعیت',
                    cell: (x) => (
                      <Badge tone={x.status === 'ACTIVE' ? 'positive' : 'negative'}>
                        {STATUS_FA[x.status]}
                      </Badge>
                    ),
                  },
                  {
                    id: 'act',
                    header: 'کار',
                    cell: (x) =>
                      x.status === 'ACTIVE' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onPress={() => setRevoke(x)}
                          data-desk-revoke={x.id}
                        >
                          لغو دستگاه
                        </Button>
                      ) : (
                        <span className="text-[11.5px] text-fg-subtle">{dt(x.revokedAt)}</span>
                      ),
                  },
                ]}
              />
            )}
          </Remote>
        </Card>
      </div>

      {create && (
        <CreateAgent
          onClose={() => setCreate(false)}
          onDone={() => {
            setCreate(false);
            reload();
          }}
        />
      )}
      {pairFor && (
        <CommandDialog
          title={`کد اتصال دستگاه برای ${pairFor.name}`}
          confirmLabel="ساخت کد اتصال"
          run={async (reason) => {
            setCode(
              await api.post<{ code: string; expiresAt: string; agent: string }>(
                `/desk/agents/${pairFor.id}/pairing`,
                { reason },
              ),
            );
          }}
          onClose={() => setPairFor(null)}
          onDone={() => {
            setPairFor(null);
            reload();
          }}
          data-desk-pairing-dialog
        >
          <Facts
            rows={[
              ['اعتبار', '۱۵ دقیقه، فقط یک بار'],
              ['کد قبلی', 'اگر کد استفاده‌نشده‌ای مانده باشد باطل می‌شود'],
              [
                'روی آیفون',
                'اول desk.hopex.ir را به صفحه‌ی اصلی اضافه کنید، بعد کد را داخل برنامه وارد کنید',
              ],
            ]}
          />
        </CommandDialog>
      )}
      {code && <PairingCode code={code} onClose={() => setCode(null)} />}
      {grants && (
        <AgentGrants
          agent={grants}
          onClose={() => setGrants(null)}
          onDone={() => {
            setGrants(null);
            reload();
          }}
        />
      )}
      {rename && (
        <RenameAgent
          agent={rename}
          onClose={() => setRename(null)}
          onDone={() => {
            setRename(null);
            reload();
          }}
        />
      )}
      {toggle && (
        <CommandDialog
          title={
            toggle.status === 'ACTIVE'
              ? `غیرفعال کردن ${toggle.displayName}`
              : `فعال کردن ${toggle.displayName}`
          }
          tone={toggle.status === 'ACTIVE' ? 'danger' : 'positive'}
          run={async (reason) => {
            await api.post(`/desk/agents/${toggle.id}/active`, {
              active: toggle.status !== 'ACTIVE',
              reason,
            });
          }}
          onClose={() => setToggle(null)}
          onDone={() => {
            setToggle(null);
            reload();
          }}
        >
          {toggle.status === 'ACTIVE' && (
            <p className="m-0 text-[13px] text-fg-muted">
              همه‌ی دستگاه‌ها و نشست‌های این کارشناس همین الان قطع می‌شوند.
            </p>
          )}
        </CommandDialog>
      )}
      {revoke && (
        <CommandDialog
          title="لغو دستگاه میز پشتیبانی"
          tone="danger"
          confirmLabel="لغو فوری دستگاه"
          run={async (reason) => {
            await api.post(`/desk/devices/${revoke.id}/revoke`, { reason });
          }}
          onClose={() => setRevoke(null)}
          onDone={() => {
            setRevoke(null);
            reload();
          }}
        >
          <Facts
            rows={[
              ['کارشناس', revoke.agent],
              ['دستگاه', `${revoke.label} · ${revoke.device ?? ''}`],
              ['اتصال', dt(revoke.pairedAt)],
            ]}
          />
        </CommandDialog>
      )}
    </AppContainer>
  );
}

const GROUPS: { key: string; label: string; hint: string }[] = [
  { key: 'KYC', label: 'احراز هویت', hint: 'تأیید، رد و اصلاح احراز سطح ۱ و ۲' },
  {
    key: 'TOMAN',
    label: 'واریز و برداشت تومان',
    hint: 'تأیید و رد واریزها، پرداخت یا رد برداشت‌ها',
  },
  { key: 'CRYPTO', label: 'واریز و برداشت رمزارز', hint: 'تأیید واریز رمزارز، ارسال یا رد برداشت' },
  { key: 'BALANCE', label: 'تغییر موجودی', hint: 'افزایش یا کاهش دستی موجودی کاربر' },
  { key: 'ACCOUNTS', label: 'حساب کاربران', hint: 'مسدود و فعال کردن، شماره، کارت و شبا' },
];

/**
 * OD-71: what an agent may do in the desk «عملیات» besides support. Each command they run is the panel's
 * own (reason, audit under their name) and is signed with their device key.
 */
function AgentGrants({
  agent,
  onClose,
  onDone,
}: {
  agent: Agent;
  onClose: () => void;
  onDone: () => void;
}) {
  const [sel, setSel] = useState<string[]>(agent.groups ?? []);
  const flip = (k: string) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return (
    <CommandDialog
      title={`دسترسی‌های ${agent.displayName}`}
      confirmLabel="ذخیره‌ی دسترسی‌ها"
      run={async (reason) => {
        await api.post(`/desk/agents/${agent.id}/grants`, { groups: sel, reason });
      }}
      onClose={onClose}
      onDone={onDone}
      data-desk-grants-dialog
    >
      <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
        پشتیبانی همیشه فعال است. هر تیک، یک بخش از «عملیات» را در میز برای این کارشناس باز می‌کند.
      </p>
      <div className="flex flex-col gap-2">
        {GROUPS.map((g) => (
          <label
            key={g.key}
            className="flex cursor-pointer items-start gap-3 rounded-md border border-line bg-surface-1 p-3"
          >
            <input
              type="checkbox"
              checked={sel.includes(g.key)}
              onChange={() => flip(g.key)}
              className="mt-1 size-4 accent-[var(--hx-accent)]"
              data-grant={g.key}
            />
            <span>
              <span className="block text-[13.5px] font-bold text-fg">{g.label}</span>
              <span className="block text-[12px] text-fg-muted">{g.hint}</span>
            </span>
          </label>
        ))}
      </div>
    </CommandDialog>
  );
}

/** The name customers see in chats («کارشناس سارا» = the first word) — never the login username. */
function RenameAgent({
  agent,
  onClose,
  onDone,
}: {
  agent: Agent;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(agent.displayName);
  const clean = name.replace(/\s+/g, ' ').trim();
  const ok = clean.length >= 2 && clean.length <= 40 && clean !== agent.displayName;
  const first = clean.split(' ')[0] ?? '';
  return (
    <CommandDialog
      title={`نام نمایشی ${agent.displayName}`}
      confirmLabel="ذخیره‌ی نام"
      canSubmit={ok}
      run={async (reason) => {
        await api.post(`/desk/agents/${agent.id}/name`, { displayName: clean, reason });
      }}
      onClose={onClose}
      onDone={onDone}
      data-desk-rename-dialog
    >
      <TextField label="نام نمایشی (فارسی)" value={name} onChange={setName} />
      <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
        کاربران در گفتگو می‌بینند: <b className="text-fg">{first ? `کارشناس ${first}` : '—'}</b>.
        نام کاربری (<bdi dir="ltr">{agent.username}</bdi>) فقط برای گزارش‌هاست و به کاربر نشان داده
        نمی‌شود.
      </p>
    </CommandDialog>
  );
}

function CreateAgent({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const ok =
    /^[a-z][a-z0-9._-]{2,31}$/.test(username.trim().toLowerCase()) && name.trim().length >= 2;
  return (
    <CommandDialog
      title="افزودن کارشناس پشتیبانی"
      confirmLabel="ساخت کارشناس"
      canSubmit={ok}
      run={async (reason) => {
        await api.post('/desk/agents', {
          username: username.trim().toLowerCase(),
          displayName: name.trim(),
          reason,
        });
      }}
      onClose={onClose}
      onDone={onDone}
      data-desk-create-agent
    >
      <TextField
        label="نام نمایشی (کاربر در گفتگو نام کوچک را می‌بیند)"
        value={name}
        onChange={setName}
      />
      <TextField
        label="نام کاربری (لاتین)"
        value={username}
        onChange={setUsername}
        ltr
        description="مثلاً sara.r — فقط برای شناسایی در گزارش‌ها؛ رمز عبوری ندارد."
      />
      <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
        کارشناس رمز ندارد و هرگز نمی‌تواند وارد پنل مدیریت شود؛ فقط با «کد اتصال» روی گوشی خودش وارد
        میز پشتیبانی می‌شود.
      </p>
    </CommandDialog>
  );
}

function PairingCode({
  code,
  onClose,
}: {
  code: { code: string; expiresAt: string; agent: string };
  onClose: () => void;
}) {
  const link = `${DESK_URL}/pair#${code.code}`;
  const [copied, setCopied] = useState(false);
  return (
    <Dialog
      isOpen
      onOpenChange={(o) => !o && onClose()}
      title={`کد اتصال ${code.agent}`}
      footer={<Button onPress={onClose}>بستن</Button>}
    >
      <div className="flex flex-col items-center gap-3 text-center text-fg" data-desk-code>
        <p className="m-0 text-[13px] text-fg-muted">
          این کد را فقط به خود کارشناس بدهید. تا {dt(code.expiresAt)} و فقط یک بار معتبر است.
        </p>
        <bdi
          dir="ltr"
          className="hx-num rounded-[14px] border border-accent bg-accent-tint px-5 py-3 font-mono text-[28px] font-extrabold tracking-[0.2em] text-accent-text"
        >
          {code.code}
        </bdi>
        <Button
          size="sm"
          variant="secondary"
          onPress={() => {
            void navigator.clipboard?.writeText(code.code).then(() => setCopied(true));
          }}
        >
          <Copy aria-hidden className="size-3.5" /> {copied ? 'کپی شد' : 'کپی کد'}
        </Button>
        <QrCode text={link} label="QR اتصال میز پشتیبانی" />
        <p className="m-0 text-[12px] leading-6 text-fg-muted">
          روی کامپیوتر یا اندروید: QR را اسکن کنید. روی آیفون: اول برنامه را از سافاری به صفحه‌ی
          اصلی اضافه کنید، بعد کد را در خود برنامه وارد کنید (نصب‌شده و سافاری حافظه‌ی جدا دارند).
        </p>
      </div>
    </Dialog>
  );
}
